import { useSearchParams } from 'next/navigation';

import { useSendDynamicEvent } from '~features/annals/useSendEvent';
import { useTranslations } from '~i18n/hooks';
import { usePathname, useRouter } from '~i18n/navigation';
import { updateQueryParamsWithPathname } from '~utils/searchParams';

import { CUSTOM_VALUE_QUERY_PARAM_VALUE } from '../../model/constants';
import {
	isValueMultiSelect,
	isValueSimple,
	isValueSliders,
} from '../../model/guards';
import type {
	BotWizardContentDependencyStep,
	BotWizardField,
	BotWizardFieldNames,
	BotWizardStep,
	BotWizardValues,
} from '../../model/types';
import { getDependencyStep } from '../../model/utils';
import { StyledNextButton } from './styles';

const parseAnnalsValues = (
	fields: Record<BotWizardFieldNames, BotWizardField>,
	values: BotWizardValues,
	keys: BotWizardFieldNames[],
	customValues: { [key in BotWizardFieldNames]?: string }
) => {
	const returnValue: { [key: string]: string } = {};
	keys.forEach((key) => {
		const field = fields[key];
		if (isValueSimple(field, values, key)) {
			if (values[key] === CUSTOM_VALUE_QUERY_PARAM_VALUE) {
				returnValue[key] = customValues[key] || '';
			} else {
				returnValue[key] = values[key] || '';
			}
		} else if (isValueMultiSelect(field, values, key)) {
			returnValue[key] = (values[key] || []).join(',');
		} else if (isValueSliders(field, values, key)) {
			returnValue[key] = values[key]?.preset || '';
			if (
				values[key]?.preset === CUSTOM_VALUE_QUERY_PARAM_VALUE &&
				!!values[key].values
			) {
				Object.keys(values[key].values).forEach((k) => {
					returnValue[`custom_${key}_${k}`] =
						values[key]!.values[k].toString() || '';
				});
			}
		}
	});
	return returnValue;
};

export const NextButton = ({
	onGenerate,
	values,
	customValues,
	presetSteps,
	firstStep,
	fields,
	setNameError,
	isFinalPresetStep,
	isFinalPromptStep,
}: {
	onGenerate: (usingPrompt: boolean) => void;
	fields: Record<BotWizardFieldNames, BotWizardField>;
	firstStep: BotWizardStep<BotWizardFieldNames[]>;
	presetSteps: BotWizardStep<BotWizardContentDependencyStep>[];
	values: BotWizardValues;
	customValues: { [key in BotWizardFieldNames]?: string };
	setNameError: () => void;
	isFinalPresetStep: boolean;
	isFinalPromptStep: boolean;
}) => {
	const t = useTranslations('BotWizard');
	const pathname = usePathname();
	const router = useRouter();
	const searchParams = useSearchParams();
	const sendEvent = useSendDynamicEvent(undefined, {
		forceSendDemoUser: true,
		includeDemoUserMarker: true,
	});

	const getStep = () => {
		return getDependencyStep({
			step: presetSteps[+values.step! - 1].data,
			values,
			contentDependency: ['gender'],
		});
	};

	const isStepWithName = () => {
		if (values.path === '0') {
			if (values.step !== '0') {
				const step = getStep();
				const nameField = step?.find((s) => fields[s].type === 'text');
				if (nameField) {
					return true;
				}
			}
		} else {
			if (values.step === '1') {
				return true;
			}
		}
	};

	const handleClick = () => {
		if (isFinalPromptStep || isFinalPresetStep) {
			onGenerate(isFinalPromptStep);
		} else if (isStepWithName() && !values.name) {
			setNameError();
		} else {
			router.push(
				updateQueryParamsWithPathname(
					{ step: parseInt(values.step as string) + 1 },
					pathname,
					searchParams
				)
			);

			if (values.step === '0') {
				sendEvent(firstStep.annalName, {
					...parseAnnalsValues(fields, values, firstStep.data, customValues),
					design_approach: values.path === '1' ? 'free_form' : 'guided_steps',
				});
			} else if (values.path === '0') {
				const step = getStep();
				const stepNumber = parseInt(values.step as string) - 1;
				if (Number.isFinite(stepNumber)) {
					sendEvent(presetSteps[stepNumber].annalName, {
						...parseAnnalsValues(fields, values, step!, customValues),
					});
				}
			}
		}
	};
	const text =
		isFinalPresetStep || isFinalPromptStep
			? `${t('createButton')} ${(values.name as string) || ''}`
			: t('nextButton');
	return (
		<StyledNextButton size="L" onClick={handleClick}>
			{text}
		</StyledNextButton>
	);
};
